import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowLeft, Wallet, Info, Trophy, Timer, Flame, Gift, Snowflake, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import ReferralCard from "../components/ReferralCard";

/**
 * Spin the Wheel — every 4h, 10 chances per spell (unused carry to next spell,
 * wiped at 00:00 UTC). Each spin costs 1000 Goldly → credited to Hifireels
 * revenue wallet with user_id logged. 9 slices, weighted RNG heavy on 500 (80%)
 * with 1-in-1000 jackpot at 100000 Goldly.
 */

function Countdown({ target, label }) {
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  useEffect(() => {
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, (target || 0) - now);
  const h = Math.floor(secs / 3600).toString().padStart(2, "0");
  const m = Math.floor((secs % 3600) / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-white/70" data-testid="spin-countdown">
      <Timer className="w-3.5 h-3.5 text-[#FF7A00]"/>
      <span className="font-bold text-white/85">{label}</span>
      <span className="font-mono font-extrabold text-[#FF7A00]">{h}:{m}:{s}</span>
    </div>
  );
}

function Wheel({ slices, rotation, spinning }) {
  // 9 slices, each 40deg. Slice 0 is at top-right of the top slice (upper purple in image).
  // We place the pointer at the TOP of the wheel — slice 1 (top purple) is at 0deg.
  const size = 320;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 10;
  const slice_angle = 360 / slices.length;

  const paths = useMemo(() => slices.map((s, i) => {
    // Center of slice i lands at angle (i * 40) - 90 (so slice 1 = top = -90 + 40 = -50 ... hmm)
    // We orient so that when rotation=0, the pointer at top (angle=-90 from center)
    // points to slice with center angle = -90. Slice i center = i*40 - 90 - (40/2).
    // Simpler: draw slice starting at angle (i * slice_angle - 90 - slice_angle/2)
    const start_deg = i * slice_angle - 90 - slice_angle / 2;
    const end_deg = start_deg + slice_angle;
    const start = (Math.PI / 180) * start_deg;
    const end = (Math.PI / 180) * end_deg;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const large = slice_angle > 180 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    // Label position: centered in the slice at 65% radius
    const mid_deg = start_deg + slice_angle / 2;
    const mid_rad = (Math.PI / 180) * mid_deg;
    const lx = cx + (r * 0.62) * Math.cos(mid_rad);
    const ly = cy + (r * 0.62) * Math.sin(mid_rad);
    return { d, color: s.color, label: s.prize.toLocaleString(), lx, ly, isJackpot: s.prize >= 100000 };
  }), [slices, cx, cy, r, slice_angle]);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size + 34 }}>
      {/* Pointer at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10" style={{ marginTop: -2 }}>
        <div className="w-0 h-0 mx-auto"
          style={{ borderLeft: "16px solid transparent", borderRight: "16px solid transparent", borderTop: "26px solid #FF2A5F", filter: "drop-shadow(0 2px 6px rgba(255,42,95,0.6))" }}/>
      </div>
      {/* Outer glow ring */}
      <div className="absolute rounded-full" style={{ width: size, height: size, top: 30, left: 0, boxShadow: "0 0 60px rgba(255,122,0,0.35), 0 0 100px rgba(255,42,95,0.25)" }}/>
      {/* Rotating wheel */}
      <motion.svg
        width={size} height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ marginTop: 30 }}
        animate={{ rotate: rotation }}
        transition={{ duration: spinning ? 4.5 : 0, ease: [0.16, 1, 0.3, 1] }}
        data-testid="spin-wheel-svg"
      >
        {/* Gold rim */}
        <circle cx={cx} cy={cy} r={r + 6} fill="#0B0F19" stroke="#FFD700" strokeWidth={4}/>
        <circle cx={cx} cy={cy} r={r + 2} fill="none" stroke="#FF7A00" strokeWidth={2}/>
        {paths.map((p, i) => (
          <g key={i}>
            <path d={p.d} fill={p.color} stroke="rgba(0,0,0,0.15)" strokeWidth={1.5}/>
            {p.isJackpot && (
              <path d={p.d} fill="url(#jackpotShine)" opacity={0.5}>
                <animate attributeName="opacity" values="0.3;0.7;0.3" dur="1.2s" repeatCount="indefinite"/>
              </path>
            )}
            {/* Prize label */}
            <g transform={`translate(${p.lx}, ${p.ly})`}>
              <text textAnchor="middle" fontSize={11} fill="rgba(255,255,255,0.9)" fontWeight="700" y={-8}>Goldly</text>
              <circle r={20} fill="#FFD700" stroke="#8B5CF6" strokeWidth={2}/>
              <text textAnchor="middle" dominantBaseline="central" fontSize={p.label.length > 4 ? 10 : 13} fill="#0B0F19" fontWeight="900" y={2}>{p.label}</text>
            </g>
          </g>
        ))}
        <defs>
          <radialGradient id="jackpotShine">
            <stop offset="0%" stopColor="#FFF" stopOpacity="0.9"/>
            <stop offset="100%" stopColor="#FFF" stopOpacity="0"/>
          </radialGradient>
        </defs>
      </motion.svg>
      {/* Center hub with SPIN button drawn separately by parent for click target */}
    </div>
  );
}

export default function SpinWheel() {
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [lastPrize, setLastPrize] = useState(null);
  const [error, setError] = useState(null);
  const [buyingFreeze, setBuyingFreeze] = useState(false);
  const rotationRef = useRef(0);

  const load = async () => {
    try {
      const [{ data: st }, { data: lb }] = await Promise.all([
        api.get("/spin/state"),
        api.get("/spin/leaderboard"),
      ]);
      setState(st);
      setLeaderboard(lb);
      if (st.granted_free_spin_today) {
        toast.success("🎉 7-day streak reward! You earned 1 FREE spin");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load spin state");
    }
  };

  useEffect(() => { load(); }, []);

  const doSpin = async () => {
    if (spinning || !state) return;
    const hasFree = (state.free_spins || 0) > 0;
    if (!hasFree && state.chances_remaining <= 0) {
      setError("No spins left in this spell. Come back after the countdown.");
      return;
    }
    if (!hasFree && (state.wallet_goldly || 0) < state.cost_per_spin) {
      setError("insufficient");
      return;
    }
    setError(null);
    setLastPrize(null);
    setSpinning(true);
    try {
      const { data } = await api.post("/spin/play");
      // Rotate so that the pointer (top) lands on the returned slice.
      // Slice i center angle from top = i * 40deg (clockwise). To bring slice i to top,
      // we need the wheel to rotate BACKWARD by that angle from its current rest.
      const slice_angle = 360 / state.slices.length;
      const target_slice_angle = -(data.slice_idx * slice_angle);
      // Add multiple full spins for drama + tiny jitter within the slice
      const jitter = (Math.random() - 0.5) * (slice_angle * 0.6);
      const full_spins = 6;
      const targetRotation = rotationRef.current + (360 * full_spins) + target_slice_angle + jitter;
      rotationRef.current = targetRotation;
      setRotation(targetRotation);

      // Wait for animation to complete
      setTimeout(() => {
        setSpinning(false);
        setLastPrize({ prize: data.prize, isJackpot: data.prize >= 100000, wasFree: data.used_free_spin });
        setState(s => ({ ...s, chances_remaining: data.chances_remaining, wallet_goldly: data.wallet_goldly, free_spins: data.free_spins }));
        if (data.prize >= 100000) {
          toast.success(`🎉 JACKPOT! You won ${data.prize.toLocaleString()} Goldly!`);
          api.get("/spin/leaderboard").then(({ data: lb }) => setLeaderboard(lb)).catch(() => {});
        } else if (data.prize >= 2000) {
          toast.success(`🎊 Nice! +${data.prize.toLocaleString()} Goldly`);
          api.get("/spin/leaderboard").then(({ data: lb }) => setLeaderboard(lb)).catch(() => {});
        } else {
          toast(`+${data.prize.toLocaleString()} Goldly`);
        }
      }, 4600);
    } catch (e) {
      setSpinning(false);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        setError("insufficient");
      } else {
        toast.error(detail || "Spin failed");
      }
    }
  };

  if (!state) {
    return <div className="min-h-[100dvh] flex items-center justify-center text-white/60" data-testid="spin-loading">Loading…</div>;
  }

  return (
    <div className="min-h-[100dvh] pb-24 bg-gradient-to-b from-[#1a0b2e] via-[#0B0F19] to-[#0B0F19] text-white overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur bg-[#0B0F19]/70 border-b border-white/10">
        <div className="max-w-md mx-auto flex items-center gap-3 p-3">
          <button onClick={() => nav(-1)} data-testid="spin-back" className="p-2 rounded-full hover:bg-white/10 active:scale-95">
            <ArrowLeft className="w-5 h-5"/>
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-outfit font-extrabold text-lg bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00] bg-clip-text text-transparent">Spin & Win</div>
            <div className="text-[11px] text-white/60">Win up to 100,000 Goldly · Jackpot slice</div>
          </div>
          <button onClick={() => nav("/wallet")} data-testid="spin-wallet" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-extrabold">
            <Wallet className="w-3.5 h-3.5 text-[#FF7A00]"/>{(state.wallet_goldly || 0).toLocaleString()}
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4">
        {/* Chances + Countdown + Free spins + Streak */}
        <div className="flex items-center justify-between glass rounded-2xl p-3 mb-3 border border-white/10">
          <div>
            <div className="text-[11px] text-white/55 font-bold uppercase tracking-wide">Chances</div>
            <div className="font-outfit font-black text-2xl" data-testid="spin-chances">
              <span className="bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00] bg-clip-text text-transparent">{state.chances_remaining}</span>
              <span className="text-white/40 text-lg"> left</span>
            </div>
          </div>
          <div className="text-right">
            <Countdown target={state.next_spell} label="Next spell in"/>
            <div className="text-[10px] text-white/45 mt-0.5">+10 fresh chances every 4h</div>
          </div>
        </div>

        {/* Free spins + streak strip */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className={`rounded-2xl p-2.5 border ${(state.free_spins || 0) > 0 ? "bg-gradient-to-r from-[#FBBF24]/25 to-[#FF7A00]/25 border-[#FBBF24]/50" : "bg-white/5 border-white/10"}`} data-testid="spin-free-card">
            <div className="flex items-center gap-1.5">
              <Gift className={`w-3.5 h-3.5 ${(state.free_spins || 0) > 0 ? "text-[#FBBF24]" : "text-white/40"}`}/>
              <div className="text-[10px] font-bold uppercase tracking-wide text-white/70">Free spins</div>
            </div>
            <div className="font-outfit font-black text-xl mt-0.5" data-testid="spin-free-count">
              {state.free_spins || 0}
              <span className="text-[10px] font-normal text-white/55 ml-1">no Goldly cost</span>
            </div>
          </div>
          <div className="rounded-2xl p-2.5 bg-white/5 border border-white/10" data-testid="spin-streak-card">
            <div className="flex items-center gap-1.5">
              <Flame className="w-3.5 h-3.5 text-[#FF2A5F]"/>
              <div className="text-[10px] font-bold uppercase tracking-wide text-white/70">Login streak</div>
            </div>
            <div className="font-outfit font-black text-xl mt-0.5">
              <span className="bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00] bg-clip-text text-transparent">{state.streak_days || 0}</span>
              <span className="text-[10px] font-normal text-white/55 ml-1">day{(state.streak_days || 0) === 1 ? "" : "s"}</span>
            </div>
            <div className="text-[10px] text-white/50">
              {state.days_to_next_free_spin === 7 ? "Free spin earned! 🎁" : `${state.days_to_next_free_spin} to next free spin`}
            </div>
          </div>
        </div>

        {/* Wheel */}
        <div className="flex flex-col items-center relative">
          <Wheel slices={state.slices} rotation={rotation} spinning={spinning}/>
          {/* Absolute-centered SPIN button on top of the wheel */}
          <button
            onClick={doSpin}
            disabled={spinning || ((state.free_spins || 0) === 0 && state.chances_remaining <= 0)}
            data-testid="spin-button"
            className="absolute top-[176px] w-24 h-24 rounded-full flex flex-col items-center justify-center font-outfit font-black text-white active:scale-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: (state.free_spins || 0) > 0
                ? "radial-gradient(circle at 30% 30%, #FBBF24, #F97316)"
                : "radial-gradient(circle at 30% 30%, #A855F7, #6D28D9)",
              boxShadow: (state.free_spins || 0) > 0
                ? "0 0 40px rgba(251,191,36,0.7), inset 0 -6px 12px rgba(0,0,0,0.4), inset 0 4px 10px rgba(255,255,255,0.25)"
                : "0 0 40px rgba(168,85,247,0.7), inset 0 -6px 12px rgba(0,0,0,0.4), inset 0 4px 10px rgba(255,255,255,0.25)",
              border: "3px solid #FFD700",
            }}
          >
            <span className="text-lg tracking-wider">{spinning ? "…" : "SPIN"}</span>
            {!spinning && (
              (state.free_spins || 0) > 0
                ? <span className="text-[9px] text-white mt-0.5 font-black">FREE!</span>
                : <span className="text-[9px] text-white/80 mt-0.5">-{state.cost_per_spin}</span>
            )}
          </button>
        </div>

        {/* Prize / Error banner */}
        <AnimatePresence>
          {lastPrize && !spinning && (
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              data-testid="spin-prize-toast"
              className={`mt-6 p-4 rounded-2xl border text-center ${lastPrize.isJackpot
                ? "bg-gradient-to-r from-[#FBBF24] to-[#FF7A00] border-white/30 text-black"
                : "bg-gradient-to-r from-[#FF2A5F]/25 to-[#FF7A00]/25 border-[#FF7A00]/40"}`}>
              <div className="flex items-center justify-center gap-2 font-outfit font-black text-lg">
                {lastPrize.isJackpot ? <Trophy className="w-5 h-5"/> : <Sparkles className="w-5 h-5 text-[#FF7A00]"/>}
                {lastPrize.isJackpot ? "JACKPOT!" : "You won"}
              </div>
              <div className="font-outfit font-black text-3xl mt-1">+{lastPrize.prize.toLocaleString()} <span className="text-base opacity-80">Goldly</span></div>
            </motion.div>
          )}
        </AnimatePresence>

        {error === "insufficient" && (
          <div className="mt-6 p-4 rounded-2xl bg-[#FF2A5F]/15 border border-[#FF2A5F]/40" data-testid="spin-error-insufficient">
            <div className="font-outfit font-extrabold text-[#FF2A5F] mb-1">Not enough Goldly</div>
            <div className="text-sm text-white/75 mb-3">You need {state.cost_per_spin.toLocaleString()} Goldly to spin. Top up your wallet to continue.</div>
            <button onClick={() => nav("/wallet")} data-testid="spin-topup" className="w-full px-4 py-2.5 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00] text-white font-extrabold active:scale-95">
              Top up wallet
            </button>
          </div>
        )}
        {error && error !== "insufficient" && (
          <div className="mt-6 p-3 rounded-2xl bg-white/5 border border-white/15 text-sm text-white/75 text-center" data-testid="spin-error">
            {error}
          </div>
        )}

        {/* Referral card — get FREE spins by inviting friends */}
        <div className="mt-6">
          <ReferralCard/>
        </div>

        {/* Streak Freeze — Store item */}
        <div className="mt-4 rounded-2xl p-4 bg-gradient-to-br from-[#0EA5E9]/20 via-[#8B5CF6]/15 to-[#00E5FF]/15 border border-[#00E5FF]/30" data-testid="streak-freeze-card">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
              <Snowflake className="w-4 h-4 text-black"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-outfit font-extrabold text-sm">Streak Freeze</div>
              <div className="text-[11px] text-white/60">Saves your streak on a missed day · 1 use per ISO week</div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <div className="text-xs" data-testid="streak-freeze-owned">
              You own <b className="text-[#00E5FF]">{state.streak_freezes_owned || 0}</b> · Max 4
            </div>
            <button onClick={async () => {
                if (buyingFreeze) return;
                setBuyingFreeze(true);
                try {
                  const { data } = await api.post("/spin/buy-streak-freeze");
                  setState(s => ({ ...s, wallet_goldly: data.wallet_goldly, streak_freezes_owned: data.streak_freezes_owned }));
                  toast.success("🧊 Streak Freeze added");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not buy Streak Freeze");
                } finally { setBuyingFreeze(false); }
              }}
              disabled={buyingFreeze || (state.streak_freezes_owned || 0) >= 4}
              data-testid="streak-freeze-buy"
              className="ml-auto px-3 py-1.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8B5CF6] text-black text-xs font-extrabold active:scale-95 disabled:opacity-50 flex items-center gap-1">
              {buyingFreeze ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Snowflake className="w-3.5 h-3.5"/>}
              Buy · {(state.streak_freeze_cost || 5000).toLocaleString()} Goldly
            </button>
          </div>
        </div>

        {/* Weekly Leaderboard — always visible; shows empty state if no data yet */}
        <div className="mt-6 glass rounded-2xl p-4 border border-white/10" data-testid="spin-leaderboard">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-[#FBBF24]"/>
            <div className="font-outfit font-extrabold text-sm">Top Winners This Week</div>
            <span className="ml-auto text-[10px] text-white/45">Last {leaderboard?.window_days || 7}d</span>
          </div>

          {(leaderboard?.jackpots?.length || 0) > 0 && (
            <div className="mb-3 p-2 rounded-xl bg-gradient-to-r from-[#FBBF24]/15 to-[#FF7A00]/15 border border-[#FBBF24]/30">
              <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide text-[#FBBF24] mb-1.5">
                <Trophy className="w-3 h-3"/>Jackpot winners
              </div>
              {leaderboard.jackpots.slice(0, 3).map((j, i) => (
                <div key={i} className="flex items-center gap-2 py-1" data-testid={`spin-jackpot-row-${i}`}>
                  <img src={j.avatar_url || "/logo192.png"} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0"/>
                  <div className="flex-1 min-w-0 text-xs">
                    <span className="font-bold text-white truncate">{j.user_name || j.user_username || "Anon"}</span>
                    <span className="text-white/40 text-[10px] ml-1">@{j.user_username || "user"}</span>
                  </div>
                  <div className="font-outfit font-black text-sm text-[#FBBF24]">+{j.prize.toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}

          {(leaderboard?.top_winners?.length || 0) > 0 ? (
            <div className="space-y-1.5">
              {leaderboard.top_winners.slice(0, 5).map((w, i) => (
                <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/5" data-testid={`spin-top-row-${i}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0 ${i === 0 ? "bg-[#FBBF24] text-black" : i === 1 ? "bg-white/70 text-black" : i === 2 ? "bg-[#CD7F32] text-black" : "bg-white/10 text-white/60"}`}>{i + 1}</div>
                  <img src={w.avatar_url || "/logo192.png"} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0"/>
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="font-bold text-white truncate">{w.user_name || w.user_username || "Anon"}</div>
                    <div className="text-[10px] text-white/45 truncate">@{w.user_username || "user"}</div>
                  </div>
                  <div className="font-outfit font-black text-sm bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00] bg-clip-text text-transparent">+{w.prize.toLocaleString()}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6" data-testid="spin-leaderboard-empty">
              <Trophy className="w-10 h-10 text-white/15 mx-auto mb-2"/>
              <div className="text-xs text-white/60 font-bold">Be the first winner!</div>
              <div className="text-[11px] text-white/40 mt-1">Big prizes (2,000+) and jackpots (100,000) will appear here.</div>
              <div className="text-[11px] text-white/40 mt-0.5">Spin now for your chance to top the board 🏆</div>
            </div>
          )}
        </div>

        {/* Rules */}
        <div className="mt-6 glass rounded-2xl p-4 border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4 text-[#FF7A00]"/>
            <div className="font-outfit font-extrabold text-sm">How it works</div>
          </div>
          <ul className="text-[12px] text-white/70 space-y-1.5 leading-snug">
            <li>• Each spin costs <b className="text-[#FF7A00]">1,000 Goldly</b>. Prize is auto-credited to your wallet.</li>
            <li>• You get <b className="text-white">10 chances every 4 hours</b> (6 spells / day).</li>
            <li>• Unused chances <b className="text-white">carry forward to the next spell</b> only.</li>
            <li>• All chances reset at <b className="text-white">00:00 UTC daily</b>.</li>
            <li>• Log in <b className="text-[#FBBF24]">7 days in a row</b> to earn a FREE spin (no Goldly deducted).</li>
            <li>• Prizes range from <b>500 Goldly</b> to the rare <b className="text-[#FBBF24]">100,000 Goldly Jackpot</b>.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
